import React, { useEffect, useState } from "react";
import { NavLink, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Col, CardBody, Card } from "reactstrap";
import Tabs, { TabPane } from "rc-tabs";
import TabContent from "rc-tabs/lib/SwipeableTabContent";
import ScrollableInkTabBar from "rc-tabs/lib/ScrollableInkTabBar";
import axios from "axios";
import LoadingOverlay from "react-loading-overlay-ts";
import DNALoader from "../../components/Loaders/DnaLoader";
import TabGenralDoctor from "./TabGenralDoctor";
import TabFinancialDoctor from "./TabFinancialDoctor";
import TabDocument from "./TabDocument";
import DoctorStatsPanel from "./DoctorStatsPanel";
import AccountCredentialActions from "../../components/AccountCredentials/AccountCredentialActions";
import { SensitivePageBanner } from "../../context/SensitiveInfoContext";

const API_BASE_URL = process.env.REACT_APP_APIURL || process.env.REACT_APP_LOGINURL;

const DoctorDetails = () => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState('overview');
    const [loading] = useState(false);
    const [statsLoading, setStatsLoading] = useState(true);
    const [doctorStats, setDoctorStats] = useState(null);
    const [doctor, setDoctor] = useState(null);
    const { id } = useParams();

    const fetchDoctorStats = async () => {
        try {
            setStatsLoading(true);
            const response = await axios.get(`${API_BASE_URL}/DoctorModule/stats/${id}`);
            if (response.data.success) {
                setDoctorStats(response.data.data);
                setDoctor(response.data.data.account);
            }
        } catch (error) {
            console.error("Error fetching doctor stats:", error);
        } finally {
            setStatsLoading(false);
        }
    };

    useEffect(() => {
        fetchDoctorStats();
    }, [id]);

    const handleTabChange = (key) => {
        setActiveTab(key);
    };

    return (
        <div className="app-main__outer">
            <div className="app-main__inner">
                <div className="bg-light-gray bg-animation">
                    <div className="d-flex justify-content-center align-items-center">
                        <Col md="12" className="mx-auto app-login-box">
                            <div className="app-page-title">
                                <div className="page-title-wrapper">
                                    <div className="page-title-heading fw-bold">
                                        <div className="page-title-icon">
                                            <i className="pe-7s-news-paper icon-gradient bg-tempting-azure" />
                                        </div>
                                        <div>
                                            {doctor?.full_name || `${t("Service Provider")} - ${t("vSARA")}`}
                                            <div className="page-title-subheading">
                                                {t("Doctor details, statistics & management")}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="page-title-actions">
                                        <AccountCredentialActions accountType="doctor" accountId={id} />
                                        <NavLink to="/doctors-list">
                                            <button type="button" className="btn btn-outline-success btn-hover-shine me-3">
                                                {t("Back")}
                                            </button>
                                        </NavLink>
                                    </div>
                                </div>
                            </div>

                            <div className="d-flex justify-content-center">
                                <div className="col-md-12">
                                    <Card className="main-card mb-3">
                                        <CardBody>
                                            <SensitivePageBanner />
                                            {doctor && (
                                                <div className="d-flex align-items-center mb-4 pb-3 border-bottom">
                                                    <div
                                                        className="rounded-circle d-flex align-items-center justify-content-center text-white me-3"
                                                        style={{
                                                            width: 64,
                                                            height: 64,
                                                            backgroundColor: '#6c757d',
                                                            fontSize: '24px',
                                                            fontWeight: 'bold',
                                                        }}
                                                    >
                                                        {doctor.full_name?.charAt(0)?.toUpperCase() || "D"}
                                                    </div>
                                                    <div>
                                                        <h4 className="mb-1 fw-bold">{doctor.full_name}</h4>
                                                        <div className="d-flex align-items-center flex-wrap gap-2">
                                                            <span className={`badge ${doctor.status === 'Active' ? 'bg-success' : doctor.status === 'Inactive' ? 'bg-danger' : 'bg-warning'}`}>
                                                                {doctor.status}
                                                            </span>
                                                            <span className="text-muted small">{doctor.specialization || doctor.type}</span>
                                                            {doctor.clinic_name && (
                                                                <span className="text-muted small">· {doctor.clinic_name}</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            <LoadingOverlay
                                                tag="div"
                                                active={loading}
                                                styles={{ overlay: (base) => ({ ...base }) }}
                                                spinner={<DNALoader />}
                                            >
                                                <Tabs
                                                    activeKey={activeTab}
                                                    onChange={handleTabChange}
                                                    renderTabBar={() => (
                                                        <ScrollableInkTabBar
                                                            extraContent={<div style={{ width: 50 }}></div>}
                                                            scrollAnimated={true}
                                                        />
                                                    )}
                                                    renderTabContent={() => <TabContent animated={false} />}
                                                >
                                                    <TabPane tab={t('Overview')} key="overview">
                                                        <DoctorStatsPanel
                                                            section="overview"
                                                            stats={doctorStats}
                                                            loading={statsLoading}
                                                            t={t}
                                                            onRefresh={fetchDoctorStats}
                                                        />
                                                    </TabPane>

                                                    <TabPane tab={t('Appointments & Patients')} key="appointments">
                                                        <DoctorStatsPanel
                                                            section="appointments"
                                                            stats={doctorStats}
                                                            loading={statsLoading}
                                                            t={t}
                                                        />
                                                    </TabPane>

                                                    <TabPane tab={t('Financial')} key="financial">
                                                        <DoctorStatsPanel
                                                            section="financial"
                                                            stats={doctorStats}
                                                            loading={statsLoading}
                                                            t={t}
                                                        />
                                                        <TabFinancialDoctor business_id={id} />
                                                    </TabPane>

                                                    <TabPane tab={t('Profile')} key="profile">
                                                        <TabGenralDoctor className="btn-custom-color" business_id={id} />
                                                    </TabPane>

                                                    <TabPane tab={t('Documents')} key="documents">
                                                        <Card>
                                                            <CardBody>
                                                                <TabDocument />
                                                            </CardBody>
                                                        </Card>
                                                    </TabPane>
                                                </Tabs>
                                            </LoadingOverlay>
                                        </CardBody>
                                    </Card>
                                </div>
                            </div>
                        </Col>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DoctorDetails;
